import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { BillingPeriod, Constants, DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";
import { ITariffPlan, ITariffPlanSubscription } from "@eagerdog/interfaces";
import { Config } from "@eagerdog/config";

import { apiService } from "src/services";
import { helperService } from "src/services/helper.service";
import Button from "src/components/Button/Button";
import LoadingSpinner from "src/components/LoadingSpinner";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./TariffPlans.module.scss";

const stripePromise = loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY ?? "");

const TariffPlan: React.FC = () => {
  const navigate = useNavigate();
  const user = helperService.getUser();
  const club = helperService.getClub();
  const isLoggedIn = helperService.isLoggedIn();

  const [loading, setLoading] = useState(false);
  const [tariffPlans, setTariffPlans] = useState<ITariffPlan[]>([]);
  const [subscription, setSubscription] = useState<ITariffPlanSubscription | null>(null);

  const [selectedPlan, setSelectedPlan] = useState<{ plan: ITariffPlan; billingPeriod: BillingPeriod } | null>(null);
  const [clientSecret, setClientSecret] = useState("");

  const ownerBenefits = [
    "Enhanced dog profiles",
    "Access to all title trackers",
    "Add manual runs to track progress outside of Eager Dog",
    "Share profiles with others",
  ];

  const clubBenefits = [
    "Unlimited pricing for Courses & Classes",
    "Enhanced CRM & Communication tools",
  ];

  const benefits = user.user_type === Constants.user_type.club_manager ? clubBenefits : ownerBenefits;

  // Dog Owner Premium is retired; owners landing here (old links, emails, mobile
  // "Learn about Premium") get a notice instead of plans. Clubs still buy here.
  const ownerPremiumRetired = isLoggedIn && !DOG_OWNER_PREMIUM_ENABLED && user.user_type === Constants.user_type.owner;

  useEffect(() => {
    // Plans are per user type and checkout needs an account — send guests to
    // log in and come straight back here (mobile "Learn about Premium" lands here).
    if (!isLoggedIn) {
      navigate("/login?r=/premium", { replace: true });
      return;
    }
    if (ownerPremiumRetired) {
      return;
    }

    setLoading(true);
    apiService.getTariffPlans()
      .then((response) => {
        // Get the Pro plan (or the first plan if only one exists)
        const proPlan = response.find(p => p.type === "pro") || response[0];
        if (proPlan) {
          setTariffPlans([proPlan]);
        }

        // For club managers, check club subscription; for owners, check personal subscription
        const clubId = user.user_type === Constants.user_type.club_manager ? club?._id : undefined;
        apiService.getUserSubscriptionPlan(clubId)
          .then((response) => {
            setSubscription(response);
          })
          .catch(() => {
            // User doesn't have a subscription - that's fine
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        setLoading(false);
        toast.show({
          title: "Plans",
          content: "Could not load plans, please try again later",
          duration: 5000,
          type: "fail"
        });
      })
  }, [isLoggedIn, navigate, user.user_type, club?._id, ownerPremiumRetired]);

  useEffect(() => {
    if (selectedPlan) {
      setLoading(true);

      const price = selectedPlan.plan.prices.find((p) => p.billing_period === selectedPlan.billingPeriod);

      if (price) {
        apiService.createCheckoutSession({
          user_id: user._id,
          tariff_plan_id: selectedPlan.plan._id,
          price_id: price.provider_external_id ?? "",
          trial_days: selectedPlan.plan.plan_config.trial_period_days ?? 0,
          title_trackers: "All",
          club_id: user.user_type === Constants.user_type.club_manager ? club?._id : undefined,
        })
          .then(({ clientSecret }) => {
            setClientSecret(clientSecret);
          })
          .catch((error) => {
            toast.show({
              title: "Checkout",
              content: "Could not start checkout, please try again later",
              errorDetails: error,
              duration: 5000,
              type: "fail"
            });
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [user._id, user.user_type, club?._id, selectedPlan]);

  const formatPrice = (plan: ITariffPlan, billingPeriod: BillingPeriod) => {
    const price = plan.prices.find((p) => p.billing_period === billingPeriod);

    if (price) {
      const formatter = new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: price.currency,
        minimumFractionDigits: 2,
      });

      return formatter.format(price.amount_cents / 100);
    }

    return "";
  };

  const getMonthlyEquivalent = (plan: ITariffPlan) => {
    const yearlyPrice = plan.prices.find((p) => p.billing_period === BillingPeriod.YEARLY);
    
    if (yearlyPrice) {
      const monthlyEquivalent = yearlyPrice.amount_cents / 12 / 100;
      const formatter = new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: yearlyPrice.currency,
        minimumFractionDigits: 2,
      });

      return formatter.format(monthlyEquivalent);
    }

    return "";
  };

  if (ownerPremiumRetired) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Premium is now free</h1>
          <p className={styles.subtitle}>
            Ribbon Room, Stats, Run History and title tracking are now included for every Eager Dog account.
            If you had a Dog Owner Premium subscription it has been cancelled and you won't be billed again.
          </p>
        </div>
        <Button onClick={() => navigate("/account")}>Go to My Dogs</Button>
      </div>
    );
  }

  if (loading && !selectedPlan) {
    return <LoadingSpinner/>;
  }

  if (!!subscription) {
    const link = user.user_type === "owner" ? "/account?t=6" : `/k/${club._id}/manage/billing`;

    return (
      <div className={styles.container}>
        <h4>You already have an active subscription</h4>
        <Link to={link}>Subscription settings</Link>
      </div>
    )
  }

  const plan = tariffPlans[0];

  return (
    <>
      {!loading && !selectedPlan && plan && (
        <div className={styles.container}>
          <div className={styles.header}>
            <h1 className={styles.title}>Upgrade to Premium</h1>
            <p className={styles.subtitle}>Get the most out of Eager Dog with our Premium subscription</p>
          </div>

          <div className={styles.benefitsSection}>
            <h3 className={styles.benefitsTitle}>What's included:</h3>
            <ul className={styles.benefitsList}>
              {benefits.map((benefit, index) => (
                <li key={index} className={styles.benefitItem}>{benefit}</li>
              ))}
            </ul>
          </div>

          <div className={styles.pricingCards}>
            {/* Annual Card */}
            <div className={`${styles.pricingCard} ${styles.recommended}`}>
              <div className={styles.saveBadge}>Best Value</div>
              <h2 className={styles.planName}>Annual</h2>
              <div className={styles.price}>{formatPrice(plan, BillingPeriod.YEARLY)}</div>
              <div className={styles.priceDescription}>per year</div>
              <div className={styles.monthlyEquivalent}>
                Just {getMonthlyEquivalent(plan)}/month
              </div>
              <div className={styles.cardFooter}>
                <Button
                  className={styles.subscribeButton}
                  onClick={() => setSelectedPlan({ plan, billingPeriod: BillingPeriod.YEARLY })}
                >
                  Subscribe
                </Button>
                {!!plan.plan_config.trial_period_days && (
                  <div className={styles.trial}>
                    {`Comes with ${plan.plan_config.trial_period_days}-day trial period`}
                  </div>
                )}
              </div>
            </div>

            {/* Monthly Card */}
            <div className={styles.pricingCard}>
              <h2 className={styles.planName}>Monthly</h2>
              <div className={styles.price}>{formatPrice(plan, BillingPeriod.MONTHLY)}</div>
              <div className={styles.priceDescription}>per month</div>
              <div className={styles.cardFooter}>
                <Button
                  className={styles.subscribeButton}
                  onClick={() => setSelectedPlan({ plan, billingPeriod: BillingPeriod.MONTHLY })}
                >
                  Subscribe
                </Button>
                {!!plan.plan_config.trial_period_days && (
                  <div className={styles.trial}>
                    {`Comes with ${plan.plan_config.trial_period_days}-day trial period`}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !!selectedPlan && (
        <div className={styles.checkoutContainer}>
          <Button
            className={styles.changePlan}
            onClick={() => {
              setSelectedPlan(null);
              setClientSecret("");
            }}
          >
            ← Back to Plans
          </Button>
          <div id="checkout" style={{ width: "100%", maxWidth: "600px" }}>
            {clientSecret && (
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ clientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            )}
          </div>
        </div>
      )}

      {loading && !!selectedPlan && <LoadingSpinner />}
    </>
  )
}

export { TariffPlan }
